
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export const useMapUrl = (initialLocation: string | null = null) => {
  const [mapsApiKey, setMapsApiKey] = useState<string | null>(null);
  const [isLoadingKey, setIsLoadingKey] = useState(false);
  const [mapUrl, setMapUrl] = useState<string | null>(null);
  const [mapEmbedUrl, setMapEmbedUrl] = useState<string | null>(null);
  const [locationInputType, setLocationInputType] = useState<'coordinates' | 'url'>('coordinates');

  useEffect(() => {
    const fetchMapsApiKey = async () => {
      setIsLoadingKey(true);
      try {
        console.log('[useMapUrl] Fetching Google Maps API key from edge function');
        const { data, error } = await supabase.functions.invoke('get-maps-api-key');
        
        if (error) {
          console.error('[useMapUrl] Error fetching Maps API key:', error);
          return;
        }
        
        console.log('[useMapUrl] Successfully received Maps API key');
        setMapsApiKey(data.apiKey);

        // Generate map URLs if we have an initial location
        if (initialLocation) {
          console.log('[useMapUrl] Processing initial location:', initialLocation);
          const url = generateMapUrl(initialLocation);
          console.log('[useMapUrl] Generated map URL:', url);
          setMapUrl(url);
          
          const embedUrl = generateMapEmbedUrl(initialLocation, data.apiKey);
          console.log('[useMapUrl] Generated embed URL:', embedUrl);
          setMapEmbedUrl(embedUrl);
        } else {
          console.log('[useMapUrl] No initial location provided');
        }
      } catch (error) {
        console.error('[useMapUrl] Failed to fetch Maps API key:', error);
      } finally {
        setIsLoadingKey(false);
      }
    };

    fetchMapsApiKey();
  }, [initialLocation]);

  const isGoogleMapsUrl = (value: string): boolean => {
    return value.includes('google.com/maps') || 
           value.includes('maps.app.goo.gl') || 
           value.includes('goo.gl/maps');
  };

  const generateMapUrl = (locationString: string): string | null => {
    if (!locationString) return null;
    
    try {
      if (isGoogleMapsUrl(locationString)) {
        return locationString;
      }
      
      const coordsPattern = /^(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)$/;
      
      if (coordsPattern.test(locationString)) {
        const [lat, lng] = locationString.split(',').map(coord => coord.trim());
        return `https://www.google.com/maps?q=${lat},${lng}`;
      }
      
      return null;
    } catch (error) {
      console.error("Error generating map URL:", error);
      return null;
    }
  };

  const generateMapEmbedUrl = (locationString: string, apiKey: string | null = mapsApiKey): string | null => {
    console.log('[useMapUrl] generateMapEmbedUrl called with:', { locationString, hasApiKey: !!apiKey });
    
    if (!locationString || !apiKey) {
      console.log('[useMapUrl] Missing location or API key:', { locationString, hasApiKey: !!apiKey });
      return null;
    }
    
    try {
      // For short Google Maps links, pass them directly without encoding (like UnifiedBookingDialog)
      if (locationString.includes('maps.app.goo.gl') || locationString.includes('goo.gl/maps')) {
        const url = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${locationString}`;
        console.log('[useMapUrl] Generated URL for short link (no encoding):', url);
        return url;
      }
      
      if (isGoogleMapsUrl(locationString)) {
        const coordsRegex = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
        const match = locationString.match(coordsRegex);
        
        if (match && match.length >= 3) {
          const lat = match[1];
          const lng = match[2];
          const url = `https://www.google.com/maps/embed/v1/view?key=${apiKey}&center=${lat},${lng}&zoom=15`;
          console.log('[useMapUrl] Generated URL for coordinates from Google Maps URL:', url);
          return url;
        }
        
        const url = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(locationString)}`;
        console.log('[useMapUrl] Generated URL for Google Maps URL:', url);
        return url;
      }
      
      const coordsPattern = /^(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)$/;
      
      if (coordsPattern.test(locationString)) {
        const [lat, lng] = locationString.split(',').map(coord => coord.trim());
        const url = `https://www.google.com/maps/embed/v1/view?key=${apiKey}&center=${lat},${lng}&zoom=15`;
        console.log('[useMapUrl] Generated URL for coordinate string:', url);
        return url;
      }
      
      // Fallback: try to use the location string directly as a place query (like UnifiedBookingDialog)
      const fallbackUrl = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(locationString)}`;
      console.log('[useMapUrl] Using fallback approach for location string:', fallbackUrl);
      return fallbackUrl;
      
    } catch (error) {
      console.error("[useMapUrl] Error generating map embed URL:", error);
      return null;
    }
  };

  const handleLocationChange = (value: string) => {
    try {
      if (isGoogleMapsUrl(value)) {
        setLocationInputType('url');
      } else if (/^(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)$/.test(value)) {
        setLocationInputType('coordinates');
      }
      
      console.log("Location input:", value);
      
      const url = generateMapUrl(value);
      console.log("Generated map URL:", url);
      setMapUrl(url);
      
      if (mapsApiKey) {
        const embedUrl = generateMapEmbedUrl(value);
        console.log("Generated map embed URL:", embedUrl);
        setMapEmbedUrl(embedUrl);
      }
    } catch (error) {
      console.error("Error in handleLocationChange:", error);
    }
  };

  return {
    mapsApiKey,
    isLoadingKey,
    mapUrl,
    mapEmbedUrl,
    locationInputType,
    handleLocationChange
  };
};
